import {useEffect, useState} from 'react';
import {
  Card as MuiCard,
  Box,
  Typography as Typo,
  styled,
  TypographyProps,
  Skeleton,
  Grid,
} from '@mui/material';
import {useSelector, useDispatch} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {saveUserSettings} from '@jobseeker/common-core/reducers/user/thunkServices';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import Checkbox from '@jobseeker/lotus/elements/CheckBox';
import Button from '@jobseeker/lotus/elements/Button';
import {Image} from '@jobseeker/lotus/components/Image';
import {BlankTickHirist, CheckHirist} from '@jobseeker/lotus/assets/icons';
import {enqueueSnackbar} from 'notistack';
import Loader from '@jobseeker/lotus/components/Loader';

type Setting = {
  heading: string;
  key: string;
};

type OtherSettingWidgetProps = {
  loading: boolean;
};

const settings: Setting[] = [
  {
    heading: 'Include cover letter while applying',
    key: 'coverLetter',
  },
  {
    heading: 'Hide stories for App',
    key: 'hideStories',
  },
  {
    heading: 'Allow similar course enquiry',
    key: 'similarCourseEnquiry',
  },
];

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));

const Typography = styled(Typo)<TypographyProps>(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const OtherSettingWidget: React.FC<OtherSettingWidgetProps> = ({loading}) => {
  const dispatch = useDispatch();
  const isLarge = useIsLargeDevice();
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  // Fetch user settings from Redux
  const userSettings = useSelector(
    (state: AppState) => state.user.profile.settings
  );

  // Initialize state with user settings from Redux
  const [updatedSettings, setUpdatedSettings] = useState(userSettings);

  // Sync component state with Redux state
  useEffect(() => {
    setUpdatedSettings(userSettings);
  }, [userSettings]);

  useEffect(() => {
    console.log('Updated Settings:', updatedSettings);
  }, [updatedSettings]);

  const handleSettingChange = (key: string) => {
    setUpdatedSettings(prev => {
      const newValue = prev[key] === 1 ? 0 : 1;
      console.log(`Updating ${key} to ${newValue}`);
      return {
        ...prev,
        [key]: newValue,
      };
    });
  };

  const handleSaveSettings = async () => {
    const payload = {...updatedSettings};

    try {
      setButtonLoading(true);
      await dispatch(saveUserSettings(payload)).unwrap();
      enqueueSnackbar('Settings have been saved successfully.', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('Failed to save settings.', {variant: 'error'});
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <Box height="100%" px={{xxs: 1.25, md: 0}}>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          px: {xxs: 1.875, md: 4.625},
          pb: {xxs: 1.875, md: 4.625},
          marginLeft: {md: 0.625},
          height: '100%',
        }}
      >
        <Box>
          {isLarge && (
            <Typography
              fontSize={{xxs: 16, md: 20}}
              fontWeight={400}
              m={0}
              mb={1}
              mt={2.75}
              sx={{
                wordWrap: 'break-word',
                textTransform: 'capitalize',
              }}
            >
              Job & Other Settings
            </Typography>
          )}
          <Grid container spacing={2} pt={1.25}>
            {settings.map((ele, index) => (
              <Grid key={index} item xs={12} md={6}>
                {loading ? (
                  <Box display="flex" justifyContent="space-between" pr={3}>
                    <Box width="100%">
                      <Skeleton
                        variant="rounded"
                        width="50%"
                        height={16}
                        sx={{mt: {xxs: 0, md: 1}, mb: 1}}
                      />
                      <Skeleton
                        variant="rounded"
                        width="30%"
                        height={12}
                        sx={{mt: {xxs: 0, md: 1}, mb: 1}}
                      />
                    </Box>
                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={25}
                      sx={{mt: 1}}
                    />
                  </Box>
                ) : (
                  <Checkbox
                    id={`setting-${index}`}
                    size={18}
                    name={`setting-${index}`}
                    checked={!!updatedSettings[ele.key]}
                    onChange={() => handleSettingChange(ele.key)}
                    icon={
                      <Image
                        src={BlankTickHirist}
                        alt="unchecked"
                        width={18}
                        height={18}
                      />
                    }
                    data-testid="checkbox"
                    checkedIcon={
                      <Image
                        src={CheckHirist}
                        alt="checked"
                        width={18}
                        height={18}
                      />
                    }
                    label={
                      <Box ml={0.1}>
                        <Typography
                          variant="body1"
                          fontSize={{xxs: 14, md: 16}}
                          sx={{color: '#555', cursor: 'pointer'}}
                        >
                          {ele.heading}
                        </Typography>
                      </Box>
                    }
                  />
                )}
              </Grid>
            ))}
          </Grid>

          <Box my={2}>
            <Typography fontSize={{xxs: 14, md: 16}} sx={{color: '#555'}}>
              Daily Application Limit : 200
            </Typography>
          </Box>
        </Box>
      </Card>
      <Box display="flex" gap={1.5} mt={4} mb={2} ml={{xxs: 0, md: 1}}>
        <Button
          sx={{
            width: {xxs: '100%', md: '191px'},
            fontSize: '16px',
            backgroundColor: '#e9630c',
            px: 5.75,
            py: 1.375,
            height: '50px',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: '#e79e00',
            },
          }}
          variant="contained"
          onClick={handleSaveSettings}
        >
          {buttonLoading ? (
            <Loader
              size={20}
              sx={{
                color: '#ffffff !important',
              }}
            />
          ) : (
            'Save Settings'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default OtherSettingWidget;
